/**
 * Journey Levels Data
 * 
 * 20 levels across 10 phases of speech development.
 * Each phase has 2 levels: a mini-game + a kitchen scene.
 */

// ============================================================================
// PHASES - The 10 stages of speech development
// ============================================================================
export const PHASES = [
  {
    id: 1,
    name: 'First Words',
    icon: '🎈',
    color: '#22c55e',
    mlu: '1.0',
    prompting: 'Full verbal + visual',
    operant: 'Echoic',
    description: 'Learning to imitate single words',
    parentTip: 'Celebrate every attempt! Even approximations count.',
  },
  {
    id: 2,
    name: 'Action Words',
    icon: '🏃',
    color: '#3b82f6',
    mlu: '1.0',
    prompting: 'Full → Partial',
    operant: 'Echoic → Mand',
    description: 'Adding verbs and action words',
    parentTip: 'Use actions during daily routines - "eat", "drink", "go".',
  },
  {
    id: 3,
    name: 'Word Combinations',
    icon: '🔗',
    color: '#8b5cf6',
    mlu: '1.5',
    prompting: 'Partial prompts',
    operant: 'Mand',
    description: 'Putting two words together',
    parentTip: 'Model two-word phrases: "want cookie", "go car".',
  },
  {
    id: 4,
    name: 'Early Grammar',
    icon: '📝',
    color: '#f59e0b',
    mlu: '2.0',
    prompting: 'Visual only',
    operant: 'Mand',
    description: 'Using "I want" and simple grammar',
    parentTip: 'Expand their phrases: if they say "juice", you say "I want juice".',
  },
  {
    id: 5,
    name: 'Expanding Phrases',
    icon: '📈',
    color: '#ec4899',
    mlu: '2.5',
    prompting: 'Visual → Hint',
    operant: 'Mand + Tact',
    description: 'Adding details and descriptions',
    parentTip: 'Ask "what color?" or "how many?" to encourage more words.',
  },
  {
    id: 6,
    name: 'Simple Sentences',
    icon: '💬',
    color: '#14b8a6',
    mlu: '3.0',
    prompting: 'Hint only',
    operant: 'Tact',
    description: 'Making complete sentences',
    parentTip: 'Describe what you see: "The dog is running!"',
  },
  {
    id: 7,
    name: 'Complex Sentences',
    icon: '🎯',
    color: '#6366f1',
    mlu: '3.5',
    prompting: 'Minimal',
    operant: 'Tact + Intraverbal',
    description: 'Using "and", "then", "because"',
    parentTip: 'Use sequence words during activities: "First... then..."',
  },
  {
    id: 8,
    name: 'Conversation',
    icon: '🗣️',
    color: '#f43f5e',
    mlu: '4.0',
    prompting: 'None',
    operant: 'Intraverbal',
    description: 'Answering questions and chatting',
    parentTip: 'Ask open-ended questions about their day.',
  },
  {
    id: 9,
    name: 'Child-Initiated',
    icon: '⭐',
    color: '#eab308',
    mlu: '4.0+',
    prompting: 'None',
    operant: 'Intraverbal',
    description: 'Starting conversations independently',
    parentTip: 'Wait and give them space to initiate.',
  },
  {
    id: 10,
    name: 'Free Communication',
    icon: '🚀',
    color: '#06b6d4',
    mlu: '4.0+',
    prompting: 'None',
    operant: 'Spontaneous',
    description: 'Natural, flowing communication',
    parentTip: 'Have real conversations about their interests!',
  },
];

// ============================================================================
// LEVELS - 20 levels (2 per phase)
// ============================================================================
export const LEVELS = [
  // === PHASE 1: First Words ===
  {
    id: 1,
    phase: 1,
    type: 'minigame',
    scene: 'balloon',
    name: 'Pop!',
    target: 'pop',
    acceptedWords: ['pop', 'bop', 'pup'],
    instruction: 'Say POP to pop the balloons!',
    hint: 'Say "POP"',
    audioPrompt: true,
  },
  {
    id: 2,
    phase: 1,
    type: 'kitchen',
    scene: 'kitchen',
    name: 'Open Sesame',
    target: 'open',
    acceptedWords: ['open', 'opened'],
    instruction: 'Help Pip open the fridge!',
    hint: 'Say "OPEN"',
    audioPrompt: true,
    kitchenAction: 'openFridge',
    kitchenSetup: { fridgeOpen: false },
  },

  // === PHASE 2: Action Words ===
  {
    id: 3,
    phase: 2,
    type: 'minigame',
    scene: 'fruit',
    name: 'Slice It!',
    target: 'cut',
    acceptedWords: ['cut', 'chop', 'slice'],
    instruction: 'Say CUT to slice the fruit!',
    hint: 'Say "CUT"',
    audioPrompt: true,
  },
  {
    id: 4,
    phase: 2,
    type: 'kitchen',
    scene: 'kitchen',
    name: 'Yummy!',
    target: 'eat',
    acceptedWords: ['eat', 'eating', 'bite'],
    instruction: 'Help Pip eat the apple!',
    hint: 'Say "EAT"',
    audioPrompt: true,
    kitchenAction: 'eatApple',
    kitchenSetup: { pipHolding: 'apple' },
  },

  // === PHASE 3: Word Combinations ===
  {
    id: 5,
    phase: 3,
    type: 'minigame',
    scene: 'rocket',
    name: 'Blast Off!',
    target: 'go up',
    acceptedWords: ['go up', 'up up', 'blast off', 'fly up'],
    instruction: 'Say GO UP to launch the rocket!',
    hint: 'Say "GO UP"',
    audioPrompt: true,
  },
  {
    id: 6,
    phase: 3,
    type: 'kitchen',
    scene: 'kitchen',
    name: 'Thirsty Pip',
    target: 'want juice',
    acceptedWords: ['want juice', 'get juice', 'juice please', 'more juice'],
    instruction: 'Pip is thirsty! Ask for juice!',
    hint: 'Say "WANT JUICE"',
    audioPrompt: true,
    kitchenAction: 'getJuice',
    kitchenSetup: { fridgeOpen: true, highlightItem: 'juice' },
  },

  // === PHASE 4: Early Grammar ===
  {
    id: 7,
    phase: 4,
    type: 'minigame',
    scene: 'race',
    name: 'Speed Racer',
    target: 'go fast',
    acceptedWords: ['go fast', 'drive fast', 'speed up', 'faster'],
    instruction: 'Say GO FAST to race!',
    hint: 'Say "GO FAST"',
    audioPrompt: false,
  },
  {
    id: 8,
    phase: 4,
    type: 'kitchen',
    scene: 'kitchen',
    name: 'Ask Nicely',
    target: 'I want apple',
    acceptedWords: ['i want apple', 'i want an apple', 'want apple please'],
    instruction: 'Ask Pip for an apple!',
    hint: 'Say "I WANT APPLE"',
    audioPrompt: false,
    kitchenAction: 'getApple',
    kitchenSetup: { fridgeOpen: true, highlightItem: 'apple' },
  },

  // === PHASE 5: Expanding Phrases ===
  {
    id: 9,
    phase: 5,
    type: 'minigame',
    scene: 'balloon',
    name: 'Color Pop',
    target: 'pop red one',
    acceptedWords: ['pop red one', 'pop the red one', 'red one', 'pop red'],
    instruction: 'Pop the RED balloon!',
    hint: 'Say "POP RED ONE"',
    audioPrompt: false,
    sceneConfig: { targetColor: 'red' },
  },
  {
    id: 10,
    phase: 5,
    type: 'kitchen',
    scene: 'kitchen',
    name: 'Polite Request',
    target: 'can I have juice please',
    acceptedWords: ['can i have juice please', 'may i have juice please', 'juice please', 'can i have juice'],
    instruction: 'Ask politely for juice!',
    hint: 'Say "CAN I HAVE JUICE PLEASE"',
    audioPrompt: false,
    kitchenAction: 'getJuice',
    kitchenSetup: { fridgeOpen: true },
  },

  // === PHASE 6: Simple Sentences ===
  {
    id: 11,
    phase: 6,
    type: 'minigame',
    scene: 'animal',
    name: 'Happy Animals',
    target: 'the dog is happy',
    acceptedWords: ['the dog is happy', 'dog is happy', 'happy dog'],
    instruction: 'Tell me about the dog!',
    hint: 'Say "THE DOG IS HAPPY"',
    audioPrompt: false,
    sceneConfig: { animal: 'dog', emotion: 'happy' },
  },
  {
    id: 12,
    phase: 6,
    type: 'kitchen',
    scene: 'kitchen',
    name: 'What Does Pip Want?',
    target: 'Pip wants to eat',
    acceptedWords: ['pip wants to eat', 'pip wants food', 'pip is hungry', 'he wants to eat'],
    instruction: 'What does Pip want to do?',
    hint: 'Say "PIP WANTS TO EAT"',
    audioPrompt: false,
    kitchenAction: 'showHungry',
    kitchenSetup: { pipMood: 'hungry' },
  },

  // === PHASE 7: Complex Sentences ===
  {
    id: 13,
    phase: 7,
    type: 'minigame',
    scene: 'race',
    name: 'Faster!',
    target: 'make the car go faster',
    acceptedWords: ['make the car go faster', 'make it go faster', 'car go faster', 'go faster'],
    instruction: 'Tell the car what to do!',
    hint: 'Say "MAKE THE CAR GO FASTER"',
    audioPrompt: false,
  },
  {
    id: 14,
    phase: 7,
    type: 'kitchen',
    scene: 'kitchen',
    name: 'Two Steps',
    target: 'first open then get juice',
    acceptedWords: ['first open then get juice', 'open then get juice', 'open and get juice'],
    instruction: 'Tell Pip the two steps!',
    hint: 'Say "FIRST OPEN THEN GET JUICE"',
    audioPrompt: false,
    kitchenAction: 'twoSteps',
    kitchenSetup: { fridgeOpen: false },
  },

  // === PHASE 8: Conversation ===
  {
    id: 15,
    phase: 8,
    type: 'minigame',
    scene: 'animal',
    name: 'Animal Chat',
    target: 'meow',
    acceptedWords: ['meow', 'cats say meow', 'the cat says meow', 'it says meow'],
    instruction: 'What does the cat say?',
    hint: 'Answer the question!',
    audioPrompt: false,
    sceneConfig: { animal: 'cat', question: true },
  },
  {
    id: 16,
    phase: 8,
    type: 'kitchen',
    scene: 'kitchen',
    name: 'Pip Asks',
    target: 'apple',
    acceptedWords: ['apple', 'an apple', 'eat apple', 'the apple', 'you should eat apple'],
    instruction: 'Pip asks: "What should I eat?"',
    hint: 'Answer Pip!',
    audioPrompt: false,
    kitchenAction: 'askQuestion',
    kitchenSetup: { pipQuestion: 'What should I eat?' },
  },

  // === PHASE 9: Child-Initiated ===
  {
    id: 17,
    phase: 9,
    type: 'minigame',
    scene: 'sandbox',
    name: 'You Choose!',
    target: null,
    acceptedWords: ['pop', 'jump', 'fly', 'go', 'run', 'play', 'bounce', 'spin'],
    instruction: 'Tell the toy what to do!',
    hint: 'Say any action word!',
    audioPrompt: false,
    sceneConfig: { freeChoice: true },
  },
  {
    id: 18,
    phase: 9,
    type: 'kitchen',
    scene: 'kitchen',
    name: 'Be the Chef',
    target: null,
    acceptedWords: ['open', 'close', 'eat', 'drink', 'get', 'juice', 'apple', 'milk', 'cookie'],
    instruction: 'You decide! Tell Pip what to do!',
    hint: 'Give Pip any command!',
    audioPrompt: false,
    kitchenAction: 'freePlay',
    kitchenSetup: { freePlay: true },
  },

  // === PHASE 10: Free Communication ===
  {
    id: 19,
    phase: 10,
    type: 'minigame',
    scene: 'sandbox',
    name: 'Playground',
    target: null,
    acceptedWords: null, // Accept anything
    instruction: 'Play and talk freely!',
    hint: 'Say anything you want!',
    audioPrompt: false,
    sceneConfig: { freePlay: true, playground: true },
  },
  {
    id: 20,
    phase: 10,
    type: 'kitchen',
    scene: 'kitchen',
    name: 'Free Kitchen',
    target: null,
    acceptedWords: null, // Accept anything
    instruction: 'Play with Pip however you want!',
    hint: 'Say anything!',
    audioPrompt: false,
    kitchenAction: 'freePlay',
    kitchenSetup: { freePlay: true, fullAccess: true },
  },
];

// ============================================================================
// PRAISE PHRASES - 100+ unique phrases (no repeats)
// ============================================================================
const PRAISE_PHRASES = [
  // Enthusiastic
  "Amazing!", "Great job!", "You did it!", "Fantastic!", "Wonderful!",
  "Super!", "Excellent!", "Perfect!", "Brilliant!", "Awesome!",
  "Way to go!", "You're a star!", "Incredible!", "Outstanding!",
  "Superb!", "Terrific!", "Marvelous!", "Spectacular!", "Phenomenal!",
  "You rock!", "Nailed it!", "Beautiful!", "Well done!", "Bravo!",
  "Impressive!", "Exceptional!", "Sensational!", "Fabulous!",
  
  // Encouraging
  "You're the best!", "Keep it up!", "That's right!", "Yes!",
  "Woohoo!", "Yay!", "You got it!", "Gold star!", "High five!",
  "So proud!", "Exactly!", "Spot on!", "Pip is happy!", "So smart!",
  "That's it!", "Hooray!", "Magnificent!", "Stupendous!", "Wowza!",
  
  // Playful
  "Boom!", "Kapow!", "Zing!", "Bingo!", "Jackpot!",
  "Score!", "Winner!", "Champion!", "Superstar!", "Legend!",
  "Rockstar!", "Genius!", "Wizard!", "Hero!", "Maestro!",
  
  // Warm
  "I knew you could!", "Look at you go!", "You're on fire!",
  "Nothing can stop you!", "You're unstoppable!", "Pure magic!",
  "That was beautiful!", "Music to my ears!", "Picture perfect!",
  "You make it look easy!", "Born to do this!", "Natural talent!",
  
  // Fun variations
  "Woo-hoo!", "Yahoo!", "Yippee!", "Oh yeah!", "Alright!",
  "Sweet!", "Cool!", "Rad!", "Epic!", "Sick!",
  "Fire!", "Lit!", "Dope!", "Clutch!", "Legendary!",
  
  // More enthusiastic
  "Absolutely amazing!", "Totally awesome!", "Simply the best!",
  "Top notch!", "First class!", "Grade A!", "Premium quality!",
  "Five stars!", "Ten out of ten!", "A plus plus!",
  "Chef's kiss!", "Mind blown!", "Jaw dropping!", "Show stopping!",
  "Earth shattering!", "Record breaking!", "History making!",
];

let usedPraises = [];

export const getRandomPraise = () => {
  if (usedPraises.length >= PRAISE_PHRASES.length) {
    usedPraises = [];
  }
  const available = PRAISE_PHRASES.filter(p => !usedPraises.includes(p));
  const praise = available[Math.floor(Math.random() * available.length)];
  usedPraises.push(praise);
  return praise;
};

export const resetPraiseTracking = () => {
  usedPraises = [];
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

export const getPhaseById = (id) => PHASES.find(p => p.id === id);

export const getLevelById = (id) => LEVELS.find(l => l.id === id);

export const getLevelsByPhase = (phaseId) => LEVELS.filter(l => l.phase === phaseId);

export const getPhaseForLevel = (levelId) => {
  const level = getLevelById(levelId);
  return level ? getPhaseById(level.phase) : null;
};

export const getPhaseProgress = (currentLevelId) => {
  const level = getLevelById(currentLevelId);
  if (!level) return { current: 0, total: LEVELS.length, percent: 0 };
  
  return {
    current: currentLevelId,
    total: LEVELS.length,
    percent: Math.round((currentLevelId / LEVELS.length) * 100),
    phaseNumber: level.phase,
    levelInPhase: currentLevelId - (level.phase - 1) * 2,
  };
};

export const matchesSpeech = (transcript, level) => {
  if (!transcript || !level) return { matched: false };
  
  const normalized = transcript.toLowerCase().trim();
  
  // Minimum length filter (noise reduction)
  if (normalized.length < 2) return { matched: false };
  
  // Free play levels accept anything meaningful
  if (level.acceptedWords === null) {
    return { matched: normalized.length >= 3, exact: false, freePlay: true };
  }
  
  // Check each accepted word/phrase
  for (const word of level.acceptedWords) {
    const wordLower = word.toLowerCase();
    
    // For single words, use word boundary matching
    if (!wordLower.includes(' ')) {
      const regex = new RegExp(`\\b${wordLower}\\b`, 'i');
      if (regex.test(normalized)) {
        return { matched: true, exact: normalized === wordLower };
      }
    } else {
      // For phrases, check if all words are present in order
      const words = wordLower.split(' ');
      let remaining = normalized;
      let allFound = true;
      
      for (const w of words) {
        const idx = remaining.indexOf(w);
        if (idx === -1) {
          allFound = false;
          break;
        }
        remaining = remaining.slice(idx + w.length);
      }
      
      if (allFound) {
        return { matched: true, exact: normalized === wordLower };
      }
    }
  }
  
  return { matched: false };
};

// ============================================================================
// EXPORTS
// ============================================================================

export default {
  PHASES,
  LEVELS,
  getPhaseById,
  getLevelById,
  getLevelsByPhase,
  getPhaseForLevel,
  getPhaseProgress,
  getRandomPraise,
  resetPraiseTracking,
  matchesSpeech,
};
